import { Injectable, inject } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { I18nService } from '../i18n/i18n.service';
import { REQUEST, RESPONSE } from 'src/express.tokens';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';

@Injectable({
    providedIn: 'root',
})
export class UtilService {
    private _i18n = inject(I18nService);
    private _response = inject(RESPONSE, { optional: true })!;
    private _request = inject(REQUEST, { optional: true })!;
    private _browser = inject(BrowserPlatformService);

    private _urlRegex: string =
        '(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?';

    public padNum(num: number): number | string {
        return num > 9 ? num : '0' + num;
    }

    public makeRandomString(length: number = 5, isWithDigits: boolean = true): string {
        let text = '';
        let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz' + (isWithDigits ? '0123456789' : '');

        for (let i = 0; i < length; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }

        return text;
    }

    public makeLorem(length: number, lang: string = 'en') {
        let randStr = '';
        while (randStr.length < length) {
            let wordLength = this.getRandomInt(3, 6);
            let word = this._createWord(wordLength, lang);

            if (Math.random() > 0.9) {
                word += ',';
            }

            randStr += word + ' ';
        }
        randStr = randStr.substring(0, length);
        randStr = randStr[0].toUpperCase() + randStr.substr(1);

        return randStr;
    }

    private _getRandChar(lang: string) {
        let LETTERS = lang === 'he' ? 'אבגדהוזחטיכלמנסעפצקרשתםןץףך' : 'abcdefghijklmnopqrstuvwxyz';
        let randIndex = parseInt((Math.random() * LETTERS.length).toString());
        return LETTERS.charAt(randIndex);
    }

    private _createWord(length: number, lang: string) {
        let word = '';
        while (word.length < length) {
            let randChar = this._getRandChar(lang);
            word += randChar;
        }

        return word;
    }

    public getRandomInt(min: number, max: number): number {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
    }

    public getRandomColor(): string {
        let letters = '0123456789ABCDEF'.split('');
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    }
    public getRandomSpecificColor(color: string = '#E27240', index: number = 3, letters: string[] = '0123456789ABCDEF'.split('')): string {
        return color.substring(0, index) + letters[Math.floor(Math.random() * letters.length)] + color.substring(index + 1, color.length);
    }

    //TODO: move to calendar service
    public fromNow(input: Date | string | number) {
        const date = input instanceof Date ? input : new Date(input);
        const formatter = new Intl.RelativeTimeFormat(this._i18n.getLangshort());
        const ranges = {
            years: 3600 * 24 * 365,
            months: 3600 * 24 * 30,
            weeks: 3600 * 24 * 7,
            days: 3600 * 24,
            hours: 3600,
            minutes: 60,
            seconds: 1,
        };
        const secondsElapsed = (date.getTime() - Date.now()) / 1000;
        for (let key in ranges) {
            if (ranges[key as keyof typeof ranges] < Math.abs(secondsElapsed)) {
                const delta = secondsElapsed / ranges[key as keyof typeof ranges];
                return formatter.format(Math.round(delta), key as Intl.RelativeTimeFormatUnit);
            }
        }
    }

    //TODO: move to calendar service
    public getHebrewCalander(date = Date.now(), isWithPrefix = true, isWithApostrophes = true, isGimatriaCalculate = true) {
        const regNonDigit = /[^0-9]/;

        const locale = this._i18n.getLangshort();
        const heDate = new Date(date)
            .toLocaleDateString(`${locale}-u-ca-hebrew`)
            .replace(/באדר א/g, 'באדר-א')
            .replace(/באדר ב/g, 'באדר-ב')
            .replace(/Adar I/g, 'Adar-I');
        const dateParams = heDate
            .split(' ')
            .map((param) => (regNonDigit.test(param) ? (locale === 'he' ? param.replace(/-/g, ' ').substr(1) : param.replace(/-/g, ' ')) : +param));
        return isGimatriaCalculate && locale === 'he'
            ? [
                  this.gimatriaCalculate(dateParams[0] as any, isWithPrefix, isWithApostrophes),
                  dateParams[1],
                  this.gimatriaCalculate(dateParams[2] as any, isWithPrefix, isWithApostrophes),
              ].join(' ')
            : dateParams.join(' ');
    }

    public _range(start: number, end: number) {
        return [...Array(1 + end - start).keys()].map((v) => start + v);
    }

    public generateKeyValueGimatria(start: number, end: number): { key: number; value: string }[] {
        const isHeb = this._i18n.langIsHeb();
        return this._range(start, end).map((year) => ({
            key: year,
            value: isHeb ? this.gimatriaCalculate(year) : year.toString(),
        }));
    }

    public gimatriaCalculate(num: number, isWithPrefix: boolean = true, isWithApostrophes: boolean = true): string {
        num = Math.floor(isWithPrefix ? num : num % 1000);

        if (num >= 1000) {
            return this.gimatriaCalculate(Math.floor(num / 1000)) + "'" + this.gimatriaCalculate(num % 1000);
        }

        let s1 = ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'];
        let s2 = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
        let s3 = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];

        let gimatria = s1[Math.floor(num / 100)] + s2[Math.floor((num % 100) / 10)] + s3[num % 10];

        gimatria = String(gimatria).replace(/יה/g, 'טו').replace(/יו/g, 'טז');
        if (isWithApostrophes) {
            if (gimatria.length > 1) {
                gimatria = gimatria.slice(0, -1) + '"' + gimatria.slice(-1);
            } else {
                gimatria = gimatria + "'";
            }
        }
        if (gimatria.length === 0 || gimatria === 'Na"N' || gimatria == 'NaN') {
            gimatria = num == 0 ? 'אפס' : 'לא ניתן לחישוב';
        }
        return gimatria;
    }

    public getClock(sec: number, hides: string[] = [], isMinimal?: boolean): string {
        if (sec === 0) {
            return '0:00';
        }
        if (!sec) {
            // Maybe needs to throw an error?
            return '';
        }
        const hours = hides.includes('hours')
            ? ''
            : isMinimal && sec < 3600
              ? ''
              : (isMinimal ? Math.floor((sec / 3600) % 24) : this._padNum(Math.floor((sec / 3600) % 24))) + ':';
        const minutes = hides.includes('minutes') ? '' : isMinimal && sec < 3600 ? Math.floor((sec / 60) % 60) : this._padNum(Math.floor((sec / 60) % 60));
        const seconds = hides.includes('seconds') ? '' : ':' + this._padNum(Math.floor(sec % 60));
        return hours + minutes + seconds;
    }

    private _padNum(num: number) {
        return num < 10 ? '0' + num : num;
    }

    public sortAndSlice(items: any[], length: number): any[] {
        return items.length ? items.sort(() => 0.5 - Math.random()).slice(0, length) : [];
    }

    public getWithoutContentInTags(str: string, tag: string = 'a'): string {
        // Remove starting and ending tags and have the inner content
        const reg = new RegExp(`<${tag}[^>]*>|<\\/${tag}>`, 'gi');
        return str?.replace(reg, '');
    }

    public toCapitalize(str: string): string {
        return str ? str.substring(0, 1).toUpperCase() + str.substring(1) : '';
    }

    public stopAndPrevent(ev?: Event) {
        if (ev) {
            ev.stopPropagation();
            ev.preventDefault();
        }
    }

    public swipe(idx: number, diff: number, length: number, disableLessTwo: boolean = true, moveOne: boolean = false): number {
        //Only relevant if the arrows are always available
        if (idx === 0 && diff < 0) {
            return length - 1;
        }
        if (idx === length - 1 && diff > 0) {
            //Moving one more so they see a shift
            return 0;
        } //***********************/
        if (disableLessTwo) {
            //In order to make the minimum step size 2
            if (diff < 0) {
                diff = Math.min(moveOne ? -1 : -2, diff);
            } else {
                diff = Math.max(moveOne ? 1 : 2, diff);
            }
        }

        idx += diff;
        if (idx < 0) {
            return 0;
        }
        if (idx > length - 1) {
            return length - 1;
        }
        return idx;
    }

    public stripTags(strHtml: string): string {
        const pattern = '<\\w+(\\s+("[^"]*"|\\\'[^\\\']*\'|[^>])+)?>|<\\/\\w+>';
        const reg = new RegExp(pattern, 'gi');
        return strHtml.replace(reg, '');
    }

    public isIncludes(textWords: string[], termWords: string[], ignoreCase: boolean = false): boolean {
        if (ignoreCase) {
            textWords = textWords.map((x) => x?.toLowerCase());
            termWords = termWords.map((x) => x?.toLowerCase());
        }
        return termWords.every((termWord) => textWords.some((textWord) => textWord.includes(termWord)));
    }

    public getWords(str: string): string[] {
        return this.cleanTerm(str).split(' ');
    }

    public cleanTerm(term: string) {
        return this._filterPrefixName(term.replace(/['׳",./\\]+/g, '').toLowerCase());
    }

    private _filterPrefixName(term: string): string {
        return term
            .split(' ')
            .filter((word) => word && !this._prefixes.includes(word))
            .join(' ');
    }

    private _prefixes: string[] = ['רב', 'הרב', 'הרהג', 'הגאון', 'הגא', 'פרופ', 'פרו', 'פרופסור', 'דר', 'הרבנית', 'rabbi', 'ztl'];

    //this function provides a better shuffle than arr.length ? arr.sort(() => 0.5 - Math.random())
    public shuffleArray(array: Array<any>): void {
        for (let i = array.length - 1; i > 0; i--) {
            let j = Math.floor(Math.random() * (i + 1)); // random index from 0 to i
            // source: https://javascript.info/array-methods#shuffle-an-array
            // swap elements array[i] and array[j]
            // we use "destructuring assignment" syntax to achieve that
            // you'll find more details about that syntax in later chapters
            // same can be written as:
            // let t = array[i]; array[i] = array[j]; array[j] = t
            [array[i], array[j]] = [array[j], array[i]];
        }
    }

    /**
     * Tool for easy unsubscribe from everywhere
     *
     * @param subscriptions Array of subscriptions or a single subscription
     */
    public unsubscribe(subscriptions: (Subscription | undefined)[] | Subscription | undefined | null) {
        // Safe assign variable into array
        if (!(subscriptions instanceof Array)) {
            // subscriptions = [subscriptions]; // hugs memeory for no reason!
            subscriptions?.unsubscribe();
            return;
        }

        subscriptions.forEach((subscription: Subscription | undefined) => {
            if (subscription) {
                subscription.unsubscribe();
                subscription = null!;
            }
        });
    }

    /**
     * Tool for easy unsubscribe from everywhere
     *
     * @param subscriptions Array of subscriptions or a single subscription
     */
    public unsubscribeSubject(subscriptions: Subject<any>[] | Subject<any>) {
        // Safe assign variable into array
        if (!(subscriptions instanceof Array)) {
            subscriptions = [subscriptions];
        }

        subscriptions.forEach((subscription: Subject<any>) => {
            if (subscription) {
                subscription.unsubscribe();
                subscription = null!;
            }
        });
    }

    public getIsValidURL(str: string): boolean {
        const clnStr = this._cleanLaedingSlash(str);
        let pattern = new RegExp(`^${this._urlRegex}$`, 'i'); // fragment locator
        return !!pattern.test(clnStr);
    }

    public getValidUrlsThatAreNotLinks(str: string): string[] {
        // Blank out existing <a>...</a> blocks so URLs already inside anchor tags are ignored.
        const withoutAnchors = str.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, (m) => ' '.repeat(m.length));
        // Blank out quoted attribute values too, so a url in src="..."/href='...' is never linkified.
        // Replaces a (?<!=['"]) lookbehind: Safari < 16.4 rejects lookbehind outright, and the
        // lookbehind only pushed the match past the protocol anyway (it returned "x.com" for
        // src="http://x.com"), which injected an anchor inside the attribute.
        const withoutAttrs = withoutAnchors.replace(/=\s*("[^"]*"|'[^']*')/g, (m) => ' '.repeat(m.length));
        const clnStr = this._cleanLaedingSlash(withoutAttrs);
        return clnStr.match(new RegExp(this._urlRegex, 'gi')) ?? [];
    }

    public applyYeshivaLinks(text: string): string {
        if (!text) return text;

        // Accept absolute (https://...), protocol-relative (//...), and bare (www.yeshiva.*) URLs.
        const yeshivaRegex = new RegExp('^(?:(?:https?:)?\\/\\/)?(?:www\\.)?(?:yeshiva\\.(?:co|org\\.il)|(?:en|he)\\.(?:local|staging))', 'i');

        // First, fix hrefs inside existing <a> tags so same-language yeshiva URLs become relative paths.
        text = text.replace(/(<a\b[^>]*\shref\s*=\s*["'])([^"']+)(["'])/gi, (full, pre, href, post) => {
            if (!yeshivaRegex.test(href)) return full;
            return pre + this._toLocalYeshivaUrl(href) + post;
        });

        // Then wrap plain-text yeshiva URLs as links, in ONE pass that alternates
        // "protected region" (a whole <a>...</a> block, or any single tag) with "url", and
        // rewrites only the url branch. Collecting urls and then calling text.replace(url, ...)
        // replaces the FIRST identical occurrence, which is the one inside a tag whenever the
        // same url appears both in an attribute and in the text — that corrupted the attribute.
        // The tag pattern is quote-aware, so a ">" inside an attribute value cannot end it and
        // unquoted attribute values are covered too.
        const tag = '<[a-z!\\/](?:[^>"\']|"[^"]*"|\'[^\']*\')*>';
        const scanner = new RegExp(`(<a\\b[^>]*>[\\s\\S]*?<\\/a>|${tag})|(${this._urlRegex})`, 'gi');
        return text.replace(scanner, (full: string, protectedRegion: string, url: string) => {
            if (protectedRegion || !url || !yeshivaRegex.test(url)) return full;
            const withProtocol = !url.startsWith('http') ? 'https://' + url : url;
            return `<a href="${this._toLocalYeshivaUrl(withProtocol)}" target="_blank">${withProtocol}</a>`;
        });
    }

    /**
     * If the yeshiva URL's language matches the current site language, return just the path (e.g. "/abc").
     * Otherwise return the URL unchanged.
     */
    private _toLocalYeshivaUrl(url: string): string {
        const urlLang = this._i18n.getLangFromUrl(url);
        if (!urlLang || urlLang !== this._i18n.getLang()) return url;

        // Extract the path/query/hash after the domain.
        // Accept https://host/path, //host/path, and bare host/path.
        const m = url.match(/^(?:(?:https?:)?\/\/)?[^/?#]+(\/[^\s]*)?$/i);
        return m?.[1] ?? '/';
    }

    private _cleanLaedingSlash(str: string): string {
        return str?.replace(/^\/|\/$/g, '')?.replace(/^\/|\/$/g, '');
    }

    // public getIsValidURL(string) {
    //     let url;

    //     try {
    //         url = new URL(string);
    //     } catch (_) {
    //         return false;
    //     }

    //     return url.protocol === "http:" || url.protocol === "https:";
    // }

    /**
     * Check if the enviromen is staging althoug it might be in production
     * Work on both client and server side.
     *
     * @returns Is enviroment staging
     */
    isStaging(): boolean {
        // Host on client side and server side users intercepted response from header.
        const hostIsStaging = this._browser.location?.host.includes('staging') ?? false;
        const headerIsStaging = this._response?.get('environment') === 'staging' || this._request?.headers?.['environment'] === 'staging';

        return hostIsStaging || headerIsStaging;
    }
}
